/**
 * @module adapter
 * @package @geenius/adapters/neon
 * @description Implements the Neon/Postgres adapter binding and the
 * SDK-free native SQL `DbAdapter` factory for the shared adapters contract.
 */

import type {
  DbAdapter,
  ListOptions,
  QueryCondition,
  QueryFilter,
  QueryOperator,
} from "@geenius/adapters";
import { DbError } from "@geenius/adapters";
import { NeonBindingError } from "./errors";
import type {
  CreateNeonDbAdapterOptions,
  NeonDbAdapter,
  NeonDbAdapterInput,
  NeonDbAdapterOptions,
  NeonDbFactoryOutput,
  NeonSqlClient,
  NeonSqlExecutor,
  NeonSqlResult,
  NeonSqlRow,
  NeonSqlTableConfig,
  NeonTransactionHandler,
} from "./types";

const DB_ADAPTER_METHODS = [
  "create",
  "get",
  "update",
  "delete",
  "list",
  "query",
  "count",
] as const;

const QUERY_OPERATORS = [
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "in",
  "contains",
] as const;

const SQL_IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/;

type NormalizedTable = {
  columns: Record<string, string>;
  idColumn: string;
  mode: "table" | "generic-record";
  tableSql: string;
};

type NormalizedListOptions = {
  limit?: number;
  offset?: number;
  order: "asc" | "desc";
  orderColumn?: string;
};

type Predicate = {
  params: unknown[];
  sql: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isSqlExecutor(value: unknown): value is NeonSqlExecutor {
  return typeof value === "function";
}

function isSqlClient(value: unknown): value is NeonSqlClient {
  return (
    isSqlExecutor(value) || (isRecord(value) && isSqlExecutor(value.query))
  );
}

function toSqlExecutor(client: NeonSqlClient): NeonSqlExecutor {
  if (isSqlExecutor(client)) {
    return client;
  }

  if (isRecord(client) && isSqlExecutor(client.query)) {
    return (text, params) => client.query(text, params);
  }

  throw new NeonBindingError(
    "createNeonDbAdapter expected a SQL executor or object with query().",
    "INVALID_NEON_OPTIONS",
  );
}

function isQueryOperator(value: unknown): value is QueryOperator {
  return (
    typeof value === "string" &&
    (QUERY_OPERATORS as readonly string[]).includes(value)
  );
}

function hasAdapterOption(
  value: unknown,
): value is { adapter: unknown; provider?: unknown; source?: unknown } {
  return isRecord(value) && "adapter" in value;
}

function hasFactoryOptions(
  value: unknown,
): value is CreateNeonDbAdapterOptions {
  return isRecord(value) && "client" in value && "tables" in value;
}

function assertNeonBindingMetadata(config: NeonDbAdapterInput): void {
  if (!hasAdapterOption(config)) {
    if (hasFactoryOptions(config)) {
      return;
    }

    if (!isRecord(config)) {
      throw new NeonBindingError(
        "withNeonDb expected a Neon binding object or native SQL factory options.",
      );
    }

    throw new NeonBindingError(
      "withNeonDb requires native SQL factory options or a Neon binding object with provider/source metadata; generic DbAdapters are not accepted.",
      "INVALID_NEON_OPTIONS",
    );
  }

  if (config.provider !== "neon") {
    throw new NeonBindingError(
      'withNeonDb expected provider to be "neon".',
      "INVALID_NEON_OPTIONS",
    );
  }

  if (!isRecord(config.source)) {
    throw new NeonBindingError(
      "withNeonDb expected Neon source metadata with factoryOptions.",
      "INVALID_NEON_OPTIONS",
    );
  }

  if (!("factoryOptions" in config.source)) {
    throw new NeonBindingError(
      "withNeonDb expected source.factoryOptions for Neon adapter bindings.",
      "INVALID_NEON_OPTIONS",
    );
  }

  assertFactoryOptions(config.source.factoryOptions);
}

function assertDbAdapter(value: unknown): asserts value is DbAdapter {
  if (!isRecord(value)) {
    throw new NeonBindingError(
      "withNeonDb expected a DbAdapter object or Neon factory options.",
    );
  }

  const missing = DB_ADAPTER_METHODS.filter(
    (method) => typeof value[method] !== "function",
  );

  if (missing.length > 0) {
    throw new NeonBindingError(
      `withNeonDb expected a DbAdapter with methods: ${DB_ADAPTER_METHODS.join(", ")}. Missing: ${missing.join(", ")}`,
    );
  }
}

function assertFactoryOptions(
  options: unknown,
): asserts options is CreateNeonDbAdapterOptions {
  if (!isRecord(options)) {
    throw new NeonBindingError(
      "createNeonDbAdapter expected Neon SQL factory options.",
      "INVALID_NEON_OPTIONS",
    );
  }

  if (!isSqlClient(options.client)) {
    throw new NeonBindingError(
      "createNeonDbAdapter expected a SQL executor or query client.",
      "INVALID_NEON_OPTIONS",
    );
  }

  if (!isRecord(options.tables)) {
    throw new NeonBindingError(
      "createNeonDbAdapter expected a collection-to-table registry.",
      "INVALID_NEON_OPTIONS",
    );
  }

  if (
    options.transaction !== undefined &&
    typeof options.transaction !== "function"
  ) {
    throw new NeonBindingError(
      "createNeonDbAdapter expected transaction to be a function when provided.",
      "INVALID_NEON_OPTIONS",
    );
  }

  if (
    options.idFactory !== undefined &&
    typeof options.idFactory !== "function"
  ) {
    throw new NeonBindingError(
      "createNeonDbAdapter expected idFactory to be a function when provided.",
      "INVALID_NEON_OPTIONS",
    );
  }
}

function getAdapterCandidate(config: NeonDbAdapterInput): unknown {
  if (hasFactoryOptions(config)) {
    return createNeonDbAdapter(config);
  }

  return hasAdapterOption(config) ? config.adapter : config;
}

function quoteIdentifier(identifier: string, label: string): string {
  if (!SQL_IDENTIFIER.test(identifier)) {
    throw new DbError(
      `Invalid Neon SQL identifier for ${label}: ${identifier}`,
      "QUERY_ERROR",
    );
  }

  return `"${identifier}"`;
}

function quoteQualifiedIdentifier(identifier: string, label: string): string {
  const parts = identifier.split(".");

  if (parts.some((part) => part.length === 0)) {
    throw new DbError(
      `Invalid Neon SQL identifier for ${label}: ${identifier}`,
      "QUERY_ERROR",
    );
  }

  return parts.map((part) => quoteIdentifier(part, label)).join(".");
}

function normalizeTableConfig(
  tables: Record<string, NeonSqlTableConfig>,
  collection: string,
): NormalizedTable {
  const config = tables[collection];

  if (config === undefined) {
    throw new DbError(
      `No Neon table configured for collection: ${collection}`,
      "COLLECTION_NOT_CONFIGURED",
    );
  }

  if (typeof config === "string") {
    return {
      columns: {},
      idColumn: "id",
      mode: "table",
      tableSql: quoteQualifiedIdentifier(config, collection),
    };
  }

  if (!isRecord(config) || typeof config.name !== "string") {
    throw new DbError(
      `Invalid Neon table configuration for collection: ${collection}`,
      "QUERY_ERROR",
    );
  }

  if (config.columns !== undefined && !isRecord(config.columns)) {
    throw new DbError(
      `Invalid Neon column map for collection: ${collection}`,
      "QUERY_ERROR",
    );
  }

  if (config.columns !== undefined) {
    for (const [field, column] of Object.entries(config.columns)) {
      if (typeof column !== "string") {
        // Reject non-string column targets before quoteIdentifier sees them:
        // SQL_IDENTIFIER.test() coerces booleans like `true` into the string
        // "true" (a valid identifier shape), which would silently quote as
        // `"true"` instead of the intended SQL column name.
        throw new DbError(
          `Invalid Neon column mapping for "${collection}".${field}: column target must be a string`,
          "QUERY_ERROR",
        );
      }
    }
  }

  if (config.idColumn !== undefined && typeof config.idColumn !== "string") {
    throw new DbError(
      `Invalid Neon idColumn for collection "${collection}": must be a string`,
      "QUERY_ERROR",
    );
  }

  if (
    config.mode !== undefined &&
    config.mode !== "table" &&
    config.mode !== "generic-record"
  ) {
    throw new DbError(
      `Invalid Neon table mode for collection: ${collection}`,
      "QUERY_ERROR",
    );
  }

  return {
    columns: (config.columns ?? {}) as Record<string, string>,
    idColumn: config.idColumn ?? "id",
    mode: config.mode ?? "table",
    tableSql: quoteQualifiedIdentifier(config.name, collection),
  };
}

function columnSql(table: NormalizedTable, field: string): string {
  const mapped = table.columns[field] ?? field;
  return quoteIdentifier(mapped, field);
}

function assertNonNegativeInteger(value: unknown, option: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    throw new DbError(
      `Neon list option "${option}" must be a non-negative integer`,
      "QUERY_ERROR",
    );
  }

  return value;
}

function normalizeListOptions(
  table: NormalizedTable,
  options?: ListOptions,
): NormalizedListOptions {
  if (options === undefined) {
    return { order: "asc" };
  }

  if (!isRecord(options)) {
    throw new DbError("Neon list options must be an object", "QUERY_ERROR");
  }

  if (
    options.order !== undefined &&
    options.order !== "asc" &&
    options.order !== "desc"
  ) {
    throw new DbError(
      'Neon list option "order" must be "asc" or "desc"',
      "QUERY_ERROR",
    );
  }

  const normalized: NormalizedListOptions = {
    order: options.order === "desc" ? "desc" : "asc",
  };

  if (options.limit !== undefined) {
    normalized.limit = assertNonNegativeInteger(options.limit, "limit");
  }

  if (options.offset !== undefined) {
    normalized.offset = assertNonNegativeInteger(options.offset, "offset");
  }

  if (options.orderBy !== undefined) {
    if (typeof options.orderBy !== "string" || options.orderBy.length === 0) {
      throw new DbError(
        'Neon list option "orderBy" must be a non-empty string',
        "QUERY_ERROR",
      );
    }
    normalized.orderColumn = columnSql(table, options.orderBy);
  } else if (
    normalized.limit !== undefined ||
    normalized.offset !== undefined
  ) {
    normalized.orderColumn = columnSql(table, table.idColumn);
  }

  return normalized;
}

function isComparableValue(value: unknown): value is number | string {
  return typeof value === "number" || typeof value === "string";
}

function assertQueryFilter(value: unknown): asserts value is QueryFilter {
  if (!Array.isArray(value)) {
    throw new DbError("Neon query filter must be an array", "QUERY_ERROR");
  }

  for (const condition of value) {
    if (
      !isRecord(condition) ||
      typeof condition.field !== "string" ||
      condition.field.length === 0 ||
      !isQueryOperator(condition.operator)
    ) {
      throw new DbError(
        "Neon query filter contains an invalid condition",
        "QUERY_ERROR",
      );
    }

    // Per-operator value shape checks mirror the localStorage reference so
    // gt/gte/lt/lte never reach SQL with non-comparable runtime values (e.g.
    // booleans, objects, arrays), `in` always receives an array, and
    // `contains` only operates on strings.
    switch (condition.operator) {
      case "gt":
      case "gte":
      case "lt":
      case "lte":
        if (!isComparableValue(condition.value)) {
          throw new DbError(
            `Neon query filter "${condition.operator}" requires a number or string value`,
            "QUERY_ERROR",
          );
        }
        break;
      case "in":
        if (!Array.isArray(condition.value)) {
          throw new DbError(
            'Neon query filter "in" requires an array value',
            "QUERY_ERROR",
          );
        }
        break;
      case "contains":
        if (typeof condition.value !== "string") {
          throw new DbError(
            'Neon query filter "contains" requires a string value',
            "QUERY_ERROR",
          );
        }
        break;
      default:
        break;
    }
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value)) {
    return false;
  }
  const proto = Object.getPrototypeOf(value);
  return proto === null || proto === Object.prototype;
}

function toPlainRecord(value: unknown, operation: string): NeonSqlRow {
  if (!isPlainObject(value)) {
    throw new DbError(
      `Neon ${operation} expected a plain record payload`,
      "QUERY_ERROR",
    );
  }

  return { ...value };
}

function toInsertRecord(value: unknown): NeonSqlRow {
  const record = toPlainRecord(value, "insert");

  if (Object.hasOwn(record, "id")) {
    throw new DbError(
      'Neon create payload must not include an "id" field',
      "QUERY_ERROR",
    );
  }

  return record;
}

function toUpdateRecord(
  value: unknown,
  table: NormalizedTable,
  collection: string,
): NeonSqlRow {
  const record = toPlainRecord(value, "update");
  const primaryIdFields = Object.keys(record).filter(
    (field) =>
      field === "id" || (table.columns[field] ?? field) === table.idColumn,
  );

  if (primaryIdFields.length > 0) {
    throw new DbError(
      `Neon update for "${collection}" must not include primary id fields: ${primaryIdFields.join(", ")}`,
      "QUERY_ERROR",
    );
  }

  return record;
}

function escapeLikePatternValue(value: unknown, field: string): string {
  if (typeof value !== "string") {
    throw new DbError(
      `Operator "contains" requires a string value for ${field}`,
      "QUERY_ERROR",
    );
  }

  return value.replace(/[\\%_]/g, "\\$&");
}

function rowsFromResult<TRow extends NeonSqlRow>(
  result: NeonSqlResult<TRow>,
  operation: string,
): TRow[] {
  const rows = Array.isArray(result)
    ? result
    : isRecord(result) && Array.isArray(result.rows)
      ? result.rows
      : null;

  if (!rows?.every(isRecord)) {
    throw new DbError(
      `Neon ${operation} returned a malformed SQL result`,
      "QUERY_ERROR",
    );
  }

  return rows as TRow[];
}

function coerceNullableRecord<T>(
  value: unknown,
  table: NormalizedTable,
  operation: string,
): T | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (!isRecord(value)) {
    throw new DbError(
      `Neon ${operation} returned a non-record payload`,
      "QUERY_ERROR",
    );
  }

  return fromSqlRow(table, value as NeonSqlRow) as T;
}

// Reverse the field→column mapping declared on the table so SQL rows come
// back to consumers under the public field names (and the idColumn — if
// renamed — surfaces as `id`). Tables with no `columns` config are pass-through.
function fromSqlRow(table: NormalizedTable, row: NeonSqlRow): NeonSqlRow {
  const reverseMap: Record<string, string> = {};
  for (const [field, column] of Object.entries(table.columns)) {
    reverseMap[column] = field;
  }
  if (table.idColumn !== "id") {
    reverseMap[table.idColumn] = "id";
  }

  // Fast path: no rewrites configured for this table.
  if (Object.keys(reverseMap).length === 0) return row;

  const out: NeonSqlRow = {};
  for (const [sqlKey, value] of Object.entries(row)) {
    const publicKey = reverseMap[sqlKey] ?? sqlKey;
    out[publicKey] = value;
  }
  return out;
}

function coerceCreatedRecord<T extends Record<string, unknown>>(
  value: unknown,
  table: NormalizedTable,
  collection: string,
): T & { id: string } {
  if (!isRecord(value)) {
    throw new DbError(
      `Neon insert into "${collection}" did not return a record`,
      "QUERY_ERROR",
    );
  }
  const mapped = fromSqlRow(table, value);
  if (typeof mapped.id !== "string") {
    throw new DbError(
      `Neon insert into "${collection}" did not return a record with a string id`,
      "QUERY_ERROR",
    );
  }
  return mapped as T & { id: string };
}

function coerceRecordList<T>(
  value: unknown,
  table: NormalizedTable,
  operation: string,
): T[] {
  if (!Array.isArray(value) || !value.every(isRecord)) {
    throw new DbError(
      `Neon ${operation} returned a non-record list`,
      "QUERY_ERROR",
    );
  }
  return value.map((row) => fromSqlRow(table, row as NeonSqlRow)) as T[];
}

function coerceCountResult(
  rows: readonly NeonSqlRow[],
  collection: string,
): number {
  if (rows.length !== 1 || !isRecord(rows[0]) || !("count" in rows[0])) {
    throw new DbError(
      `Neon count for "${collection}" returned an invalid count result`,
      "QUERY_ERROR",
    );
  }

  const value = rows[0].count;
  const count =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim().length > 0
        ? Number(value)
        : Number.NaN;

  if (!Number.isInteger(count) || count < 0) {
    throw new DbError(
      `Neon count for "${collection}" returned an invalid count value`,
      "QUERY_ERROR",
    );
  }

  return count;
}

function addParam(params: unknown[], value: unknown): string {
  params.push(value);
  return `$${params.length}`;
}

function createDefaultId(): string {
  const randomUUID = globalThis.crypto?.randomUUID;
  if (typeof randomUUID === "function") {
    return randomUUID.call(globalThis.crypto);
  }

  return `neon_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
}

function toGenericPayload(value: unknown, operation: string): NeonSqlRow {
  return toPlainRecord(value, operation);
}

function fromGenericRecordRow(row: unknown, operation: string): NeonSqlRow {
  if (!isRecord(row) || typeof row.id !== "string") {
    throw new DbError(
      `Neon ${operation} returned an invalid generic-record row`,
      "QUERY_ERROR",
    );
  }

  let payload = row.payload;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch (error) {
      throw new DbError(
        `Neon ${operation} returned an invalid generic-record payload`,
        "QUERY_ERROR",
        error,
      );
    }
  }
  if (!isRecord(payload)) {
    throw new DbError(
      `Neon ${operation} returned an invalid generic-record payload`,
      "QUERY_ERROR",
    );
  }

  return { ...payload, id: row.id };
}

function coerceGenericRecord<T>(value: unknown, operation: string): T | null {
  if (value === null || value === undefined) {
    return null;
  }

  return fromGenericRecordRow(value, operation) as T;
}

function coerceGenericCreatedRecord<T extends Record<string, unknown>>(
  value: unknown,
  collection: string,
): T & { id: string } {
  const record = coerceGenericRecord<T & { id: string }>(
    value,
    `insert into "${collection}"`,
  );
  if (record === null) {
    throw new DbError(
      `Neon insert into "${collection}" did not return a generic record`,
      "QUERY_ERROR",
    );
  }

  return record;
}

function coerceGenericRecordList<T>(
  rows: readonly NeonSqlRow[],
  operation: string,
): T[] {
  return rows.map((row) => fromGenericRecordRow(row, operation) as T);
}

function normalizeGenericListOptions(
  options?: ListOptions,
): NormalizedListOptions {
  const normalized = normalizeListOptions(
    {
      columns: {},
      idColumn: "id",
      mode: "table",
      tableSql: "unused",
    },
    options,
  );

  return normalized;
}

function genericFieldValueSql(params: unknown[], field: string): string {
  return `"payload" ->> ${addParam(params, field)}`;
}

function genericFieldJsonSql(params: unknown[], field: string): string {
  return `"payload" -> ${addParam(params, field)}`;
}

function genericJsonNullPredicateSql(params: unknown[], field: string): string {
  const fieldParam = addParam(params, field);
  return `("payload" ? ${fieldParam} and jsonb_typeof("payload" -> ${fieldParam}) = 'null')`;
}

function addGenericJsonValueParam(
  params: unknown[],
  value: unknown,
  field: string,
): string {
  const serialized = JSON.stringify(value);
  if (serialized === undefined) {
    throw new DbError(
      `Neon generic-record filter for "${field}" requires a JSON-serializable value`,
      "QUERY_ERROR",
    );
  }

  return `${addParam(params, serialized)}::jsonb`;
}

function buildGenericWhereClause(
  collection: string,
  filter: QueryFilter,
): Predicate {
  assertQueryFilter(filter);

  const params: unknown[] = [collection];
  const clauses = [`"collection" = $1`];

  for (const condition of filter) {
    switch (condition.operator) {
      case "eq":
        if (condition.value === null) {
          clauses.push(genericJsonNullPredicateSql(params, condition.field));
        } else {
          const valueSql = genericFieldJsonSql(params, condition.field);
          clauses.push(
            `${valueSql} = ${addGenericJsonValueParam(
              params,
              condition.value,
              condition.field,
            )}`,
          );
        }
        break;
      case "neq":
        if (condition.value === null) {
          clauses.push(
            `not ${genericJsonNullPredicateSql(params, condition.field)}`,
          );
        } else {
          const valueSql = genericFieldJsonSql(params, condition.field);
          clauses.push(
            `(${valueSql} is null or ${valueSql} <> ${addGenericJsonValueParam(
              params,
              condition.value,
              condition.field,
            )})`,
          );
        }
        break;
      case "gt":
        {
          const valueSql = genericFieldValueSql(params, condition.field);
          clauses.push(
            typeof condition.value === "number"
              ? `(${valueSql})::numeric > ${addParam(params, condition.value)}`
              : `${valueSql} > ${addParam(params, String(condition.value))}`,
          );
        }
        break;
      case "gte":
        {
          const valueSql = genericFieldValueSql(params, condition.field);
          clauses.push(
            typeof condition.value === "number"
              ? `(${valueSql})::numeric >= ${addParam(params, condition.value)}`
              : `${valueSql} >= ${addParam(params, String(condition.value))}`,
          );
        }
        break;
      case "lt":
        {
          const valueSql = genericFieldValueSql(params, condition.field);
          clauses.push(
            typeof condition.value === "number"
              ? `(${valueSql})::numeric < ${addParam(params, condition.value)}`
              : `${valueSql} < ${addParam(params, String(condition.value))}`,
          );
        }
        break;
      case "lte":
        {
          const valueSql = genericFieldValueSql(params, condition.field);
          clauses.push(
            typeof condition.value === "number"
              ? `(${valueSql})::numeric <= ${addParam(params, condition.value)}`
              : `${valueSql} <= ${addParam(params, String(condition.value))}`,
          );
        }
        break;
      case "in": {
        if (!Array.isArray(condition.value)) {
          throw new DbError(
            `Operator "in" requires an array value for ${condition.field}`,
            "QUERY_ERROR",
          );
        }
        if (condition.value.length === 0) {
          clauses.push("false");
          break;
        }
        const valueSql = genericFieldValueSql(params, condition.field);
        clauses.push(
          `${valueSql} in (${condition.value
            .map((item) => addParam(params, String(item)))
            .join(", ")})`,
        );
        break;
      }
      case "contains": {
        const valueSql = genericFieldValueSql(params, condition.field);
        clauses.push(
          `${valueSql} like ${addParam(
            params,
            `%${escapeLikePatternValue(condition.value, condition.field)}%`,
          )} escape '\\'`,
        );
        break;
      }
      default:
        throw new DbError(
          `Unsupported Neon query operator: ${condition.operator}`,
          "QUERY_ERROR",
        );
    }
  }

  return { params, sql: ` where ${clauses.join(" and ")}` };
}

function buildWhereClause(
  table: NormalizedTable,
  filter: QueryFilter,
): Predicate {
  assertQueryFilter(filter);

  if (filter.length === 0) {
    return { params: [], sql: "" };
  }

  const params: unknown[] = [];
  const clauses = filter.map((condition: QueryCondition) => {
    const column = columnSql(table, condition.field);

    switch (condition.operator) {
      case "eq":
        // SQL `= NULL` never matches; use `IS NULL` so Neon parity matches
        // the JS-equality semantics of memory/cloudflareKV adapters.
        return condition.value === null
          ? `${column} is null`
          : `${column} = ${addParam(params, condition.value)}`;
      case "neq":
        // Same: `<> NULL` never matches; use `IS NOT NULL`.
        return condition.value === null
          ? `${column} is not null`
          : `${column} <> ${addParam(params, condition.value)}`;
      case "gt":
        return `${column} > ${addParam(params, condition.value)}`;
      case "gte":
        return `${column} >= ${addParam(params, condition.value)}`;
      case "lt":
        return `${column} < ${addParam(params, condition.value)}`;
      case "lte":
        return `${column} <= ${addParam(params, condition.value)}`;
      case "in": {
        if (!Array.isArray(condition.value)) {
          throw new DbError(
            `Operator "in" requires an array value for ${condition.field}`,
            "QUERY_ERROR",
          );
        }
        if (condition.value.length === 0) {
          return "false";
        }
        // Split NULL entries from concrete values: SQL `IN (NULL)` never
        // matches NULL rows. Generate `(column IN (...) OR column IS NULL)`
        // when the in-list contains a null, matching the JS Array.includes
        // semantics used by memory/cloudflareKV.
        const hasNull = condition.value.some((v) => v === null);
        const nonNull = condition.value.filter((v) => v !== null);
        if (nonNull.length === 0) {
          // List was [null] (and possibly other nulls) — only null can match.
          return `${column} is null`;
        }
        const placeholders = nonNull.map((item) => addParam(params, item));
        const inClause = `${column} in (${placeholders.join(", ")})`;
        return hasNull ? `(${inClause} or ${column} is null)` : inClause;
      }
      case "contains":
        return `${column} like ${addParam(
          params,
          `%${escapeLikePatternValue(condition.value, condition.field)}%`,
        )} escape '\\'`;
      default:
        throw new DbError(
          `Unsupported Neon query operator: ${condition.operator}`,
          "QUERY_ERROR",
        );
    }
  });

  return { params, sql: ` where ${clauses.join(" and ")}` };
}

function getErrorText(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return typeof error === "string" ? error : "";
}

function getErrorCode(error: unknown): string | undefined {
  if (!isRecord(error)) {
    return undefined;
  }

  const code = error.code ?? error.errno;
  return typeof code === "string" ? code.toUpperCase() : undefined;
}

function isConnectionFailure(error: unknown): boolean {
  const code = getErrorCode(error);

  if (
    code !== undefined &&
    (code.startsWith("08") ||
      code === "53300" ||
      code === "57P01" ||
      code === "57P02" ||
      code === "57P03" ||
      code === "EAI_AGAIN" ||
      code === "ECONNREFUSED" ||
      code === "ECONNRESET" ||
      code === "ENETUNREACH" ||
      code === "ENOTFOUND" ||
      code === "ETIMEDOUT")
  ) {
    return true;
  }

  return /\b(connection|network|timeout|timed out|fetch failed)\b/i.test(
    getErrorText(error),
  );
}

function isConstraintFailure(error: unknown): boolean {
  const code = getErrorCode(error);
  return (
    code === "23502" ||
    code === "23503" ||
    code === "23505" ||
    code === "23514" ||
    code === "23P01"
  );
}

async function runSql<TRow extends NeonSqlRow>(
  sql: NeonSqlExecutor,
  text: string,
  params: readonly unknown[],
  operation: string,
): Promise<TRow[]> {
  try {
    return rowsFromResult(await sql<TRow>(text, params), operation);
  } catch (error) {
    if (error instanceof DbError) {
      throw error;
    }
    throw new DbError(
      `Neon ${operation} failed`,
      isConnectionFailure(error)
        ? "CONNECTION_ERROR"
        : isConstraintFailure(error)
          ? "CONSTRAINT_VIOLATION"
          : "QUERY_ERROR",
      error,
    );
  }
}

/**
 * Creates a Neon-backed database adapter using native parameterized SQL. The
 * caller owns the Neon connection and table schema; this package owns only the
 * shared `DbAdapter` behavior and SDK-free binding validation.
 */
export function createNeonDbAdapter(
  options: CreateNeonDbAdapterOptions & {
    transaction: NonNullable<CreateNeonDbAdapterOptions["transaction"]>;
  },
): NeonDbAdapter;
export function createNeonDbAdapter(
  options: CreateNeonDbAdapterOptions,
): DbAdapter;
export function createNeonDbAdapter(
  options: CreateNeonDbAdapterOptions,
): DbAdapter {
  assertFactoryOptions(options);

  const { client, idFactory, tables, transaction } = options;
  const outerSql = toSqlExecutor(client);

  const buildAdapter = (sql: NeonSqlExecutor): DbAdapter => ({
    async create<T extends Record<string, unknown>>(
      collection: string,
      data: Omit<T, "id">,
    ) {
      const table = normalizeTableConfig(tables, collection);
      const record = toInsertRecord(data);
      if (table.mode === "generic-record") {
        const id = idFactory?.() ?? createDefaultId();
        const rows = await runSql<NeonSqlRow>(
          sql,
          `insert into ${table.tableSql} ("collection", "id", "payload") values ($1, $2, $3::jsonb) returning "id", "payload"`,
          [collection, id, JSON.stringify(record)],
          `insert into "${collection}"`,
        );

        return coerceGenericCreatedRecord<T>(rows[0], collection);
      }

      const generatedId = idFactory?.() ?? createDefaultId();
      const entriesWithoutId = Object.entries(record);
      const entries: Array<[string, unknown]> = [
        [table.idColumn, generatedId],
        ...entriesWithoutId,
      ];
      const text = `insert into ${table.tableSql} (${entries
        .map(([field]) => columnSql(table, field))
        .join(", ")}) values (${entries
        .map((_, index) => `$${index + 1}`)
        .join(", ")}) returning *`;
      const rows = await runSql<T & { id: string }>(
        sql,
        text,
        entries.map(([, value]) => value),
        `insert into "${collection}"`,
      );

      return coerceCreatedRecord<T>(rows[0], table, collection);
    },

    async get<T>(collection: string, id: string) {
      const table = normalizeTableConfig(tables, collection);
      if (table.mode === "generic-record") {
        const rows = await runSql<NeonSqlRow>(
          sql,
          `select "id", "payload" from ${table.tableSql} where "collection" = $1 and "id" = $2 limit 1`,
          [collection, id],
          `get from "${collection}"`,
        );

        return coerceGenericRecord<T>(rows[0], `get from "${collection}"`);
      }

      const rows = await runSql<NeonSqlRow>(
        sql,
        `select * from ${table.tableSql} where ${columnSql(
          table,
          table.idColumn,
        )} = $1 limit 1`,
        [id],
        `get from "${collection}"`,
      );

      return coerceNullableRecord<T>(
        rows[0],
        table,
        `get from "${collection}"`,
      );
    },

    async update<T extends Record<string, unknown>>(
      collection: string,
      id: string,
      data: Partial<T>,
    ) {
      const table = normalizeTableConfig(tables, collection);
      const record = toUpdateRecord(data, table, collection);
      if (table.mode === "generic-record") {
        const rows = await runSql<NeonSqlRow>(
          sql,
          `update ${table.tableSql} set "payload" = "payload" || $3::jsonb where "collection" = $1 and "id" = $2 returning "id", "payload"`,
          [collection, id, JSON.stringify(toGenericPayload(record, "update"))],
          `update in "${collection}"`,
        );

        return coerceGenericRecord<T>(rows[0], `update in "${collection}"`);
      }

      const entries = Object.entries(record);

      if (entries.length === 0) {
        // Empty Partial<T> updates are a no-op per the DbAdapter contract;
        // sibling memory/cloudflareKV adapters return the existing record
        // without writing, so mirror that by selecting the row instead of
        // emitting an invalid `set` clause.
        const rows = await runSql<NeonSqlRow>(
          sql,
          `select * from ${table.tableSql} where ${columnSql(
            table,
            table.idColumn,
          )} = $1 limit 1`,
          [id],
          `update in "${collection}"`,
        );

        return coerceNullableRecord<T>(
          rows[0],
          table,
          `update in "${collection}"`,
        );
      }

      const params = entries.map(([, value]) => value);
      params.push(id);
      const rows = await runSql<NeonSqlRow>(
        sql,
        `update ${table.tableSql} set ${entries
          .map(([field], index) => `${columnSql(table, field)} = $${index + 1}`)
          .join(", ")} where ${columnSql(table, table.idColumn)} = $${
          params.length
        } returning *`,
        params,
        `update in "${collection}"`,
      );

      return coerceNullableRecord<T>(
        rows[0],
        table,
        `update in "${collection}"`,
      );
    },

    async delete(collection: string, id: string) {
      const table = normalizeTableConfig(tables, collection);
      if (table.mode === "generic-record") {
        const rows = await runSql<NeonSqlRow>(
          sql,
          `delete from ${table.tableSql} where "collection" = $1 and "id" = $2 returning "id"`,
          [collection, id],
          `delete from "${collection}"`,
        );

        return rows.length > 0;
      }

      const rows = await runSql<NeonSqlRow>(
        sql,
        `delete from ${table.tableSql} where ${columnSql(
          table,
          table.idColumn,
        )} = $1 returning ${columnSql(table, table.idColumn)}`,
        [id],
        `delete from "${collection}"`,
      );

      return rows.length > 0;
    },

    async list<T>(collection: string, options?: ListOptions) {
      const table = normalizeTableConfig(tables, collection);
      if (table.mode === "generic-record") {
        const normalized = normalizeGenericListOptions(options);
        const params: unknown[] = [collection];
        let text = `select "id", "payload" from ${table.tableSql} where "collection" = $1`;

        if (normalized.orderColumn !== undefined) {
          const orderField = options?.orderBy ?? "id";
          const orderColumn =
            orderField === "id"
              ? '"id"'
              : `"payload" ->> ${addParam(params, orderField)}`;
          text += ` order by ${orderColumn} ${normalized.order}`;
        }
        if (normalized.limit !== undefined) {
          text += ` limit ${addParam(params, normalized.limit)}`;
        }
        if (normalized.offset !== undefined) {
          text += ` offset ${addParam(params, normalized.offset)}`;
        }

        return coerceGenericRecordList<T>(
          await runSql<NeonSqlRow>(sql, text, params, `list "${collection}"`),
          `list "${collection}"`,
        );
      }

      const normalized = normalizeListOptions(table, options);
      const params: unknown[] = [];
      let text = `select * from ${table.tableSql}`;

      if (normalized.orderColumn !== undefined) {
        text += ` order by ${normalized.orderColumn} ${normalized.order}`;
      }
      if (normalized.limit !== undefined) {
        text += ` limit ${addParam(params, normalized.limit)}`;
      }
      if (normalized.offset !== undefined) {
        text += ` offset ${addParam(params, normalized.offset)}`;
      }

      return coerceRecordList<T>(
        await runSql<NeonSqlRow>(sql, text, params, `list "${collection}"`),
        table,
        `list "${collection}"`,
      );
    },

    async query<T>(collection: string, filter: QueryFilter) {
      const table = normalizeTableConfig(tables, collection);
      if (table.mode === "generic-record") {
        const predicate = buildGenericWhereClause(collection, filter);

        return coerceGenericRecordList<T>(
          await runSql<NeonSqlRow>(
            sql,
            `select "id", "payload" from ${table.tableSql}${predicate.sql}`,
            predicate.params,
            `query "${collection}"`,
          ),
          `query "${collection}"`,
        );
      }

      const predicate = buildWhereClause(table, filter);

      return coerceRecordList<T>(
        await runSql<NeonSqlRow>(
          sql,
          `select * from ${table.tableSql}${predicate.sql}`,
          predicate.params,
          `query "${collection}"`,
        ),
        table,
        `query "${collection}"`,
      );
    },

    async count(collection: string, filter?: QueryFilter) {
      const table = normalizeTableConfig(tables, collection);
      if (table.mode === "generic-record") {
        const predicate =
          filter === undefined
            ? { params: [collection], sql: ' where "collection" = $1' }
            : buildGenericWhereClause(collection, filter);
        const rows = await runSql<NeonSqlRow>(
          sql,
          `select count(*) as count from ${table.tableSql}${predicate.sql}`,
          predicate.params,
          `count "${collection}"`,
        );

        return coerceCountResult(rows, collection);
      }

      const predicate =
        filter === undefined
          ? { params: [], sql: "" }
          : buildWhereClause(table, filter);
      const rows = await runSql<NeonSqlRow>(
        sql,
        `select count(*) as count from ${table.tableSql}${predicate.sql}`,
        predicate.params,
        `count "${collection}"`,
      );

      return coerceCountResult(rows, collection);
    },
  });

  const adapter = buildAdapter(outerSql);

  if (!transaction) {
    return adapter;
  }

  const transactionalAdapter: NeonDbAdapter = {
    ...adapter,
    async transaction<TResult>(handler: NeonTransactionHandler<TResult>) {
      return transaction(handler, {
        adapter: transactionalAdapter,
        client,
        sql: outerSql,
        tables,
        // Build a `DbAdapter` whose CRUD operations are bound to the
        // transaction-scoped SQL executor. Runners must invoke
        // `createScopedAdapter(txSql)` inside their BEGIN/COMMIT block and
        // expose the result to the handler so `context.adapter.create/...`
        // execute inside the transaction. The legacy `adapter` field is kept
        // for back-compat but is intentionally bound to the outer client.
        createScopedAdapter: (scopedSql: NeonSqlExecutor) =>
          buildAdapter(scopedSql),
      });
    },
  };

  return transactionalAdapter;
}

function hasTransaction(adapter: DbAdapter): adapter is NeonDbAdapter {
  return (
    "transaction" in adapter &&
    typeof (adapter as { transaction?: unknown }).transaction === "function"
  );
}

/**
 * Wraps any non-`DbError` failure surfaced by the delegated adapter in a typed
 * `DbError`, preserving the original `cause` so downstream retry classification
 * and error-handling middleware can introspect it. `DbError` instances pass
 * through unchanged.
 */
async function callDelegated<T>(
  method: string,
  invoke: () => Promise<T>,
): Promise<T> {
  try {
    return await invoke();
  } catch (error) {
    if (error instanceof DbError) {
      throw error;
    }
    const detail = error instanceof Error ? `: ${error.message}` : "";
    throw new DbError(
      `Neon adapter ${method} failed${detail}`,
      "QUERY_ERROR",
      error,
    );
  }
}

/**
 * Validates or constructs a Neon `DbAdapter` for provider registries and
 * downstream app bindings. Pass native SQL factory options to build the
 * SDK-free facade locally, or pass an existing Neon adapter with provider
 * metadata returned by `@geenius/db`.
 */
export function withNeonDb(
  config: CreateNeonDbAdapterOptions & {
    transaction: NonNullable<CreateNeonDbAdapterOptions["transaction"]>;
  },
): NeonDbAdapter;
export function withNeonDb(config: CreateNeonDbAdapterOptions): DbAdapter;
export function withNeonDb(
  config: NeonDbAdapterOptions & { adapter: NeonDbFactoryOutput },
): NeonDbAdapter;
export function withNeonDb(config: NeonDbAdapterInput): DbAdapter;
export function withNeonDb(config: NeonDbAdapterInput): DbAdapter {
  assertNeonBindingMetadata(config);
  const adapter = getAdapterCandidate(config);
  assertDbAdapter(adapter);

  const wrapped: DbAdapter = {
    async create<T extends Record<string, unknown>>(
      collection: string,
      data: Omit<T, "id">,
    ) {
      return await callDelegated("create", () =>
        adapter.create<T>(collection, data),
      );
    },
    async get<T>(collection: string, id: string) {
      return await callDelegated("get", () => adapter.get<T>(collection, id));
    },
    async update<T extends Record<string, unknown>>(
      collection: string,
      id: string,
      data: Partial<T>,
    ) {
      return await callDelegated("update", () =>
        adapter.update<T>(collection, id, data),
      );
    },
    async delete(collection: string, id: string) {
      return await callDelegated("delete", () =>
        adapter.delete(collection, id),
      );
    },
    async list<T>(collection: string, options?: ListOptions) {
      return await callDelegated("list", () =>
        adapter.list<T>(collection, options),
      );
    },
    async query<T>(collection: string, filter: QueryFilter) {
      return await callDelegated("query", () =>
        adapter.query<T>(collection, filter),
      );
    },
    async count(collection: string, filter?: QueryFilter) {
      return await callDelegated("count", () =>
        adapter.count(collection, filter),
      );
    },
  };

  if (!hasTransaction(adapter)) {
    return wrapped;
  }

  const wrappedWithTransaction: NeonDbAdapter = {
    ...wrapped,
    async transaction<TResult>(handler: NeonTransactionHandler<TResult>) {
      return await callDelegated("transaction", () =>
        adapter.transaction(handler),
      );
    },
  };

  return wrappedWithTransaction;
}
